import React, { ReactNode, useMemo, useRef, useContext } from 'react';
import { NafThemeType } from '@naf/theme';
import { useSpring, animated, config } from '@react-spring/web';
import { ThemeContextValue } from '@lib/ThemeContext';
import { warnUnrenderableBlockType } from '@lib/warnUnrenderableBlockType';
import { PromotionTrackingContext } from '@lib/PromotionTrackingContext';
import { SectionBlockType } from '@appTypes/CategoryAppType';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import { useIsomorphicLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import { ColorSection } from './blockComponents';
import { blockRegistry } from './blockRegistry';
import { mapBlockTheme } from './blockTheme';

interface Props {
  content?: (SectionBlockType | null | undefined)[];
  activateContextThemeSwitch?: boolean;
}

const isSectionBlock = (block: SectionBlockType | null | undefined): block is SectionBlockType => Boolean(block?.type);

const warnUnhandledBlock = (type: string) =>
  warnUnrenderableBlockType(
    'SectionBlocks',
    type,
    'Add it to blockRegistry.tsx (and re-run `pnpm gen:sanity-map`), or remove the type from ' +
      'SectionBlockType if it is no longer published.',
  );

const Section = ({
  block,
  children,
  currentTheme,
}: {
  block: SectionBlockType;
  children: ReactNode;
  currentTheme?: NafThemeType;
}) => <ColorSection theme={mapBlockTheme(block, currentTheme)}>{children}</ColorSection>;

const Blocks = ({ block }: { block: SectionBlockType }) => {
  // hasOwn: a CMS string must not resolve to an Object.prototype member.
  const render = Object.hasOwn(blockRegistry, block.type)
    ? (blockRegistry[block.type] as (block: SectionBlockType) => ReactNode)
    : undefined;

  if (!render) {
    warnUnhandledBlock(block.type);
    return null;
  }

  return <>{render(block)}</>;
};

export const SectionBlocks = ({ content, activateContextThemeSwitch }: Props) => {
  const theme = useContext(ThemeContextValue);
  const promotionTracking = useContext(PromotionTrackingContext);
  const validContent = useMemo(() => content?.filter(isSectionBlock) ?? [], [content]);
  const hasCompareMembershipBlock = useMemo(
    () => validContent.some((block) => block.type === 'compareMembershipBlock'),
    [validContent],
  );

  useIsomorphicLayoutEffect(() => {
    if (!promotionTracking) {
      return;
    }

    promotionTracking.setDisablePromotionTracking(hasCompareMembershipBlock);

    // eslint-disable-next-line consistent-return
    return () => {
      promotionTracking.setDisablePromotionTracking(false);
    };
  }, [promotionTracking, hasCompareMembershipBlock]);

  if (validContent.length > 0) {
    return (
      <>
        {validContent.map((block) =>
          activateContextThemeSwitch ? (
            <Section block={block} key={block.key} currentTheme={theme}>
              <Blocks block={block} />
            </Section>
          ) : (
            <Blocks block={block} key={block.key} />
          ),
        )}
      </>
    );
  }
  return null;
};

export const VerticallyFadingSectionBlocks = ({ content, activateContextThemeSwitch }: Props) => {
  const theme = useContext(ThemeContextValue);
  const validContent = useMemo(() => content?.filter(isSectionBlock) ?? [], [content]);

  if (validContent.length > 0) {
    return (
      <>
        {validContent.map((block) =>
          activateContextThemeSwitch ? (
            <Section block={block} key={block.key} currentTheme={theme}>
              <VerticallyFadingBlock block={block} />
            </Section>
          ) : (
            <VerticallyFadingBlock key={block.key} block={block} />
          ),
        )}
      </>
    );
  }
  return null;
};

const VerticallyFadingBlock = ({ block }: { block: SectionBlockType }) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const dataRef = useIntersectionObserver(triggerRef, { freezeOnceVisible: true });

  const [props] = useSpring(
    () => ({
      config: config.gentle,
      from: { opacity: 0, y: 40 },
      to: {
        opacity: dataRef?.isIntersecting ? 1 : 0,
        y: dataRef?.isIntersecting ? 0 : 40,
      },
    }),
    [dataRef],
  );

  return (
    <animated.div ref={triggerRef} style={props}>
      <Blocks block={block} />
    </animated.div>
  );
};
